/**
 * AdminGiftPriming — admin-only "Gift a Priming Installation" panel.
 *
 * Why: clone a recipient's voice, generate a bespoke first-person
 * Pre-Event Priming, seed their library, return a shareable URL — in
 * one form. Turns a 2-hour engineering dance into 90 seconds.
 *
 * Route: /admin/gift-priming
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import { AdminLayout } from "../AdminPages";
import { ArrowLeft, Gift, Loader2, Check, Copy, ExternalLink, Plus, X, AlertTriangle, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const DEFAULT_AFFS = [
  "I am exactly the person they have been waiting for",
  "My calm is my edge",
  "I bring presence into every room",
  "I listen more than I speak, and I speak with weight",
  "I belong in rooms like this",
  "I am ready — and that is enough",
];

export default function AdminGiftPriming() {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Form state
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [voiceUrl, setVoiceUrl] = useState("");
  const [eventName, setEventName] = useState("");
  const [context, setContext] = useState("");
  const [affirmations, setAffirmations] = useState(DEFAULT_AFFS);
  const [perspective, setPerspective] = useState("first_person");

  // Run state
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  // Day/Night state
  const [dnBusy, setDnBusy] = useState(false);
  const [dnStatus, setDnStatus] = useState(null);  // null | "queued" | "rendering" | "ready" | "failed"
  const [dnUrls, setDnUrls] = useState(null);

  const updateAff = (i, v) => setAffirmations((prev) => prev.map((a, idx) => (idx === i ? v : a)));
  const addAff = () => setAffirmations((prev) => [...prev, ""]);
  const removeAff = (i) => setAffirmations((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError(null);
    setResult(null);
    setDnStatus(null);
    setDnUrls(null);

    const cleanAffs = affirmations.map((a) => a.trim()).filter(Boolean);
    if (!recipientName || !recipientEmail || !voiceUrl || !eventName || cleanAffs.length === 0) {
      setError("Please fill in name, email, voice URL, event name, and at least one affirmation.");
      return;
    }

    setBusy(true);
    try {
      const r = await fetch(`${API}/api/admin/gift-priming/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({
          recipient_name: recipientName,
          recipient_email: recipientEmail,
          voice_url: voiceUrl,
          event_name: eventName,
          context,
          affirmations: cleanAffs,
          voice_perspective: perspective,
        }),
      });
      if (!r.ok) {
        const errBody = await r.json().catch(() => ({}));
        throw new Error(errBody.detail || `HTTP ${r.status}`);
      }
      const data = await r.json();
      setResult(data);
    } catch (e) {
      setError(e.message || "Something went wrong");
    } finally {
      setBusy(false);
    }
  };

  const queueDayNight = async () => {
    if (!result?.installation_id) return;
    setDnBusy(true);
    setDnStatus("queued");
    try {
      const r = await fetch(`${API}/api/admin/gift-priming/${result.installation_id}/installations`, {
        method: "POST",
        headers: auth(),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      // Poll status every 15s until ready or failed
      const poll = setInterval(async () => {
        try {
          const sr = await fetch(`${API}/api/admin/gift-priming/status/${result.installation_id}`, { headers: auth() });
          if (!sr.ok) return;
          const sd = await sr.json();
          setDnStatus(sd.day_night_status || "rendering");
          if (sd.day_night_status === "ready") {
            setDnUrls(sd.full_urls || sd.urls);
            clearInterval(poll);
            setDnBusy(false);
          } else if (sd.day_night_status === "failed") {
            clearInterval(poll);
            setDnBusy(false);
            setError("Day/Night render failed — check backend logs.");
          }
        } catch (_) { /* noop */ }
      }, 15000);
    } catch (e) {
      setError(e.message);
      setDnBusy(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).catch(() => {});
  };

  if (user && user.role !== "admin") {
    return (
      <AdminLayout title="Gift a Priming">
        <div className="text-white/70 p-8">Admin access required.</div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout title="Gift a Priming">
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <button
          onClick={() => navigate("/admin")}
          className="flex items-center gap-2 text-white/60 hover:text-white text-sm"
          data-testid="gift-priming-back-btn"
        >
          <ArrowLeft size={16} /> Back to Admin
        </button>

        <header className="flex items-start gap-4">
          <div className="rounded-full bg-amber-500/15 p-3 text-amber-300 ring-1 ring-amber-400/30">
            <Gift size={24} />
          </div>
          <div>
            <h1 className="text-2xl text-white" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>
              Gift a Priming Installation
            </h1>
            <p className="text-white/55 text-sm mt-1">
              Clone a recipient&apos;s voice, generate a bespoke first-person Pre-Event Priming, seed their library. ~25-40 seconds.
            </p>
          </div>
        </header>

        {/* ── Form ─────────────────────────────────────── */}
        <div className="space-y-4 bg-white/[0.025] ring-1 ring-white/10 rounded-xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Recipient name">
              <input
                value={recipientName}
                onChange={(e) => setRecipientName(e.target.value)}
                placeholder="Charlie"
                data-testid="gift-recipient-name"
                className="input"
              />
            </Field>
            <Field label="Recipient email">
              <input
                type="email"
                value={recipientEmail}
                onChange={(e) => setRecipientEmail(e.target.value)}
                placeholder="charlie@example.com"
                data-testid="gift-recipient-email"
                className="input"
              />
            </Field>
          </div>

          <Field label="Voice recording URL (Dropbox / direct mp3/m4a)" hint="Dropbox 'dl=0' links work — auto-converted to direct download.">
            <input
              value={voiceUrl}
              onChange={(e) => setVoiceUrl(e.target.value)}
              placeholder="https://www.dropbox.com/scl/fi/..."
              data-testid="gift-voice-url"
              className="input"
            />
          </Field>

          <Field label="Event">
            <input
              value={eventName}
              onChange={(e) => setEventName(e.target.value)}
              placeholder="Interview with Elie — Global Head of Sales, Natixis"
              data-testid="gift-event-name"
              className="input"
            />
          </Field>

          <Field label="Context (optional)" hint="A few sentences. The more specific, the more personal the script.">
            <textarea
              rows={3}
              value={context}
              onChange={(e) => setContext(e.target.value)}
              placeholder="What's at stake, who's in the room, what's the moment they're walking into…"
              data-testid="gift-context"
              className="input min-h-[80px] resize-vertical"
            />
          </Field>

          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-white/80 text-sm font-medium">Affirmations</label>
              <span className="text-white/40 text-xs">{affirmations.filter((a) => a.trim()).length} active</span>
            </div>
            <div className="space-y-2">
              {affirmations.map((a, i) => (
                <div key={i} className="flex gap-2 items-center">
                  <input
                    value={a}
                    onChange={(e) => updateAff(i, e.target.value)}
                    className="input flex-1"
                    placeholder="I am ready — and that is enough"
                    data-testid={`gift-aff-${i}`}
                  />
                  <button
                    onClick={() => removeAff(i)}
                    disabled={affirmations.length <= 1}
                    className="p-2 rounded text-white/40 hover:text-white/80 hover:bg-white/5 disabled:opacity-30"
                    aria-label="Remove affirmation"
                  >
                    <X size={16} />
                  </button>
                </div>
              ))}
            </div>
            <button
              onClick={addAff}
              className="mt-2 flex items-center gap-1.5 text-amber-300/80 hover:text-amber-200 text-xs"
              data-testid="gift-add-aff"
            >
              <Plus size={14} /> Add affirmation
            </button>
          </div>

          <Field label="Voice perspective" hint="First-person when the audio is in the recipient's own cloned voice — far more powerful for self-affirmation.">
            <div className="flex gap-2">
              <PerspBtn active={perspective === "first_person"} onClick={() => setPerspective("first_person")}>
                First person · I am
              </PerspBtn>
              <PerspBtn active={perspective === "second_person"} onClick={() => setPerspective("second_person")}>
                Second person · You are
              </PerspBtn>
            </div>
          </Field>

          {error && (
            <div className="flex items-start gap-2 text-rose-300 text-sm bg-rose-500/10 ring-1 ring-rose-400/30 rounded-md p-3">
              <AlertTriangle size={16} className="mt-0.5 flex-shrink-0" />
              <span data-testid="gift-error">{error}</span>
            </div>
          )}

          <button
            onClick={submit}
            disabled={busy}
            data-testid="gift-submit-btn"
            className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-400 text-black font-medium py-3 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {busy ? (
              <>
                <Loader2 size={18} className="animate-spin" /> Cloning voice & generating priming…
              </>
            ) : (
              <>
                <Sparkles size={18} /> Generate Bespoke Priming
              </>
            )}
          </button>
        </div>

        {/* ── Result ────────────────────────────────────── */}
        {result && (
          <div className="bg-emerald-500/10 ring-1 ring-emerald-400/30 rounded-xl p-5 space-y-4" data-testid="gift-result">
            <div className="flex items-start gap-3">
              <Check size={20} className="text-emerald-300 mt-0.5" />
              <div>
                <h2 className="text-white text-lg">Priming created</h2>
                <p className="text-white/60 text-sm">Send these details to {result.recipient_email}.</p>
              </div>
            </div>

            <ResultRow label="Email"    value={result.recipient_email} />
            <ResultRow label="Password" value={result.recipient_password} mono />
            <ResultRow label="Audio URL" value={result.audio_full_url} link onCopy={() => copyToClipboard(result.audio_full_url)} />
            <ResultRow label="Installation ID" value={result.installation_id} mono />

            <div className="border-t border-white/10 pt-4 mt-4">
              <p className="text-white/70 text-sm mb-3">Also build a premium **Day + Night** installation? (~5-10 min wall clock, more ElevenLabs API spend)</p>
              <button
                onClick={queueDayNight}
                disabled={dnBusy || dnStatus === "ready"}
                data-testid="gift-day-night-btn"
                className="w-full flex items-center justify-center gap-2 bg-white/10 hover:bg-white/15 text-white py-2.5 rounded-lg transition disabled:opacity-50"
              >
                {dnBusy ? (
                  <>
                    <Loader2 size={16} className="animate-spin" />
                    Rendering Day + Night ({dnStatus || "queued"}…)
                  </>
                ) : dnStatus === "ready" ? (
                  <>
                    <Check size={16} className="text-emerald-300" /> Day + Night ready
                  </>
                ) : (
                  "Build Day + Night Installation"
                )}
              </button>
              {dnUrls && (
                <div className="mt-3 space-y-2">
                  {dnUrls.day &&       <ResultRow label="Day 30min"   value={dnUrls.day}       link onCopy={() => copyToClipboard(dnUrls.day)} />}
                  {dnUrls.night_3hr && <ResultRow label="Night 3hr"   value={dnUrls.night_3hr} link onCopy={() => copyToClipboard(dnUrls.night_3hr)} />}
                  {dnUrls.night &&     <ResultRow label="Night 30min" value={dnUrls.night}     link onCopy={() => copyToClipboard(dnUrls.night)} />}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <style>{`
        .input {
          width: 100%;
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(255,255,255,0.10);
          border-radius: 8px;
          color: rgba(255,255,255,0.92);
          padding: 9px 12px;
          font-size: 14px;
          transition: border-color 150ms ease;
        }
        .input:focus { outline: none; border-color: rgba(245,158,11,0.55); }
        .input::placeholder { color: rgba(255,255,255,0.30); }
      `}</style>
    </AdminLayout>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-white/80 text-sm font-medium mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-white/40 text-xs mt-1.5">{hint}</p>}
    </div>
  );
}

function PerspBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`flex-1 py-2 rounded-md text-sm transition ${
        active
          ? "bg-amber-500/20 text-amber-200 ring-1 ring-amber-400/40"
          : "bg-white/[0.03] text-white/60 hover:text-white/85 ring-1 ring-white/10"
      }`}
    >
      {children}
    </button>
  );
}

function ResultRow({ label, value, mono, link, onCopy }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <span className="text-white/40 text-xs uppercase tracking-wider w-32 flex-shrink-0 pt-1">{label}</span>
      <div className="flex-1 min-w-0">
        <code className={`block bg-black/30 rounded px-2 py-1.5 text-xs break-all ${mono ? "font-mono" : ""} text-white/85`}>
          {value}
        </code>
      </div>
      <div className="flex gap-1 flex-shrink-0">
        <button onClick={onCopy} className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white" title="Copy">
          <Copy size={14} />
        </button>
        {link && (
          <a href={value} target="_blank" rel="noreferrer" className="p-1.5 rounded hover:bg-white/10 text-white/60 hover:text-white" title="Open">
            <ExternalLink size={14} />
          </a>
        )}
      </div>
    </div>
  );
}
